import { Navigate, RouterProvider, createBrowserRouter, type RouteObject } from 'react-router-dom';

import { AppLayout } from './app-layout';
import { LoginPage } from '@/routes/login';
import { DashboardPage } from '@/routes/dashboard';
import { SettingsPage } from '@/routes/settings';
import { RegisterPage } from '@/routes/register';
import { ForgotPasswordPage } from '@/routes/forgot-password';
import { ResetPasswordPage } from '@/routes/reset-password';
import { CheckInPage } from '@/routes/check-in';
import { CarpeDiemPage as CarpeDiemViewPage } from '@/routes/carpe-diem';
import { ClientsPage } from '@/routes/clients';
// F-022 (2026-05-23): Step 0 collapsed to two tools — the old narrative
// DiscoveryCallPage is gone; KpiPreAssessmentPage is repurposed and lives
// at /tools/discovery-call (display name "Discovery Call / KPI Pre-Assessment").
import { KpiPreAssessmentPage } from '@/routes/tools/kpi-pre-assessment';
import { ClientCommitmentPage } from '@/routes/tools/client-commitment';
import { CompassPage } from '@/routes/tools/compass';
import { EulogyPage } from '@/routes/tools/eulogy';
import { SuperheroPage } from '@/routes/tools/superhero';
import { ValuesCardSortPage } from '@/routes/tools/values-card-sort';
import { ScrambledVirtuesPage } from '@/routes/tools/scrambled-virtues';
import { GoodDayFallbackDayPage } from '@/routes/tools/good-day-fallback-day';
import { ChaosScenarioCardsPage } from '@/routes/tools/chaos-scenario-cards';
import { IdentitySnapshotPage } from '@/routes/tools/identity-snapshot';
import { VirtueShortlistPage } from '@/routes/tools/virtue-shortlist';
import { VirtueAffirmationsPage } from '@/routes/tools/virtue-affirmations';
import { WheelOfLifePage } from '@/routes/tools/wheel-of-life';
import { HeroVisionPage } from '@/routes/tools/hero-vision';
import { WoopPage } from '@/routes/tools/woop';
import { EnergyAuditPage } from '@/routes/tools/energy-audit';
import { MasterpieceDayPage } from '@/routes/tools/masterpiece-day';
import { MasterpieceBlueprintPage } from '@/routes/tools/masterpiece-blueprint';
import { FixedBookendPage } from '@/routes/tools/fixed-bookend';
import { SixStepReframingPage } from '@/routes/tools/six-step-reframing';
import { VirtueReassessmentPage } from '@/routes/tools/virtue-reassessment';
import { KpiCorrelationPage } from '@/routes/tools/kpi-correlation';
import { BidForConnectionPage } from '@/routes/tools/bid-for-connection';
import { HeroicWorkAuditPage } from '@/routes/tools/heroic-work-audit';
import { DragonTriggerLogPage } from '@/routes/tools/dragon-trigger-log';
import { WeeklyReviewPage } from '@/routes/tools/weekly-review';
import { WeeklyReviewV2Page } from '@/routes/tools/weekly-review-v2';
import { LetterToFutureSelfPage } from '@/routes/tools/letter-to-future-self';
import { CelebrationSystemPage } from '@/routes/tools/celebration-system';
import { HerosJourneyPage } from '@/routes/tools/heros-journey';
import { IdentityGapReassessmentPage } from '@/routes/tools/identity-gap-reassessment';
import { LegacyLetterPage } from '@/routes/tools/legacy-letter';
import { FinishedToolView } from '@/routes/tools/finished-tool-view';
import { StatusPage } from '@/routes/status';
import { AdminImportClientsPage } from '@/routes/admin-import-clients';
import { MessagesPage } from '@/routes/messages';
import { SettingsNotificationsPage } from '@/routes/settings-notifications';
import { SettingsSecurityTwoFactorPage } from '@/routes/settings-security-2fa';
import { AdminRetentionPage } from '@/routes/admin-retention';
import { CoachQuickViewPage } from '@/routes/coach-quick-view';
import { ProtectedRoute } from '@/components/protected-route';
import { StepGate } from '@/components/step-gate';

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      // Password reset (USR-007). `/reset?token=…` is the link the API puts
      // in the reset e-mail; the old login link pointed at
      // /auth/password-reset/request, kept as an alias for stale tabs.
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset', element: <ResetPasswordPage /> },
      { path: 'auth/password-reset/request', element: <Navigate to="/forgot-password" replace /> },
      // Public availability indicator (NFR-096) — no ProtectedRoute wrapper.
      { path: 'status', element: <StatusPage /> },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        // NL-001 + NL-003 — Daily Check-in unlocks at Step 1; below that
        // step, render the StepLockedCard fallback instead. Coaches
        // bypass the gate via useClientStep returning step=null.
        path: 'check-in',
        element: (
          <ProtectedRoute>
            <StepGate requiredStep={1} sectionKey="dailyCheckIn">
              <CheckInPage />
            </StepGate>
          </ProtectedRoute>
        ),
      },
      {
        // F-030 follow-up + NL-001 + NL-007 — client read-only Carpe
        // Diem matrix. Unlocks at Step 2; gated below that step.
        path: 'carpe-diem',
        element: (
          <ProtectedRoute>
            <StepGate requiredStep={2} sectionKey="carpeDiem">
              <CarpeDiemViewPage />
            </StepGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'clients',
        element: (
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/import-clients',
        element: (
          <ProtectedRoute>
            <AdminImportClientsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'coach/quick-view',
        element: (
          <ProtectedRoute>
            <CoachQuickViewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/notifications',
        element: (
          <ProtectedRoute>
            <SettingsNotificationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/security/2fa',
        element: (
          <ProtectedRoute>
            <SettingsSecurityTwoFactorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/retention',
        element: (
          <ProtectedRoute>
            <AdminRetentionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'messages',
        element: (
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'coach/clients/:clientId/messages',
        element: (
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        ),
      },
      {
        // F-022 — Discovery Call / KPI Pre-Assessment (merged).
        path: 'tools/discovery-call',
        element: (
          <ProtectedRoute>
            <KpiPreAssessmentPage />
          </ProtectedRoute>
        ),
      },
      {
        // F-022 — Client Commitment Agreement (new).
        path: 'tools/client-commitment',
        element: (
          <ProtectedRoute>
            <ClientCommitmentPage />
          </ProtectedRoute>
        ),
      },
      {
        // F-022 back-compat — keep the old URL working as a redirect.
        path: 'tools/kpi-pre-assessment',
        element: (
          <ProtectedRoute>
            <KpiPreAssessmentPage />
          </ProtectedRoute>
        ),
      },
      {
        // F-024 — Compass (Step 1 entry point).
        path: 'tools/compass',
        element: (
          <ProtectedRoute>
            <CompassPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/eulogy',
        element: (
          <ProtectedRoute>
            <EulogyPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/superhero',
        element: (
          <ProtectedRoute>
            <SuperheroPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/values-card-sort',
        element: (
          <ProtectedRoute>
            <ValuesCardSortPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/scrambled-virtues',
        element: (
          <ProtectedRoute>
            <ScrambledVirtuesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/good-day-fallback-day',
        element: (
          <ProtectedRoute>
            <GoodDayFallbackDayPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/chaos-scenario-cards',
        element: (
          <ProtectedRoute>
            <ChaosScenarioCardsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/identity-snapshot',
        element: (
          <ProtectedRoute>
            <IdentitySnapshotPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/virtue-shortlist',
        element: (
          <ProtectedRoute>
            <VirtueShortlistPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/virtue-affirmations',
        element: (
          <ProtectedRoute>
            <VirtueAffirmationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/wheel-of-life',
        element: (
          <ProtectedRoute>
            <WheelOfLifePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/hero-vision',
        element: (
          <ProtectedRoute>
            <HeroVisionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/woop',
        element: (
          <ProtectedRoute>
            <WoopPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/masterpiece-blueprint',
        element: (
          <ProtectedRoute>
            <MasterpieceBlueprintPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/energy-audit',
        element: (
          <ProtectedRoute>
            <EnergyAuditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/masterpiece-day',
        element: (
          <ProtectedRoute>
            <MasterpieceDayPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/fixed-bookend',
        element: (
          <ProtectedRoute>
            <FixedBookendPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/six-step-reframing',
        element: (
          <ProtectedRoute>
            <SixStepReframingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/virtue-reassessment',
        element: (
          <ProtectedRoute>
            <VirtueReassessmentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/kpi-correlation',
        element: (
          <ProtectedRoute>
            <KpiCorrelationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/bid-for-connection',
        element: (
          <ProtectedRoute>
            <BidForConnectionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/heroic-work-audit',
        element: (
          <ProtectedRoute>
            <HeroicWorkAuditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/dragon-trigger-log',
        element: (
          <ProtectedRoute>
            <DragonTriggerLogPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/weekly-review',
        element: (
          <ProtectedRoute>
            <WeeklyReviewPage />
          </ProtectedRoute>
        ),
      },
      {
        // NL-041 — Step 8 Weekly Review v2 (structured 3-prompt
        // reflection persisted to weekly_reviews.reflection_*).
        path: 'tools/weekly-review-v2',
        element: (
          <ProtectedRoute>
            <WeeklyReviewV2Page />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/celebration-system',
        element: (
          <ProtectedRoute>
            <CelebrationSystemPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/heros-journey',
        element: (
          <ProtectedRoute>
            <HerosJourneyPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/identity-gap-reassessment',
        element: (
          <ProtectedRoute>
            <IdentityGapReassessmentPage />
          </ProtectedRoute>
        ),
      },
      {
        // NL-044 — Step 9 Letter to Future Self (versioned
        // client_documents row with doc_type='future_self_letter').
        // NOT sealed — distinct from /tools/legacy-letter.
        path: 'tools/letter-to-future-self',
        element: (
          <ProtectedRoute>
            <LetterToFutureSelfPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tools/legacy-letter',
        element: (
          <ProtectedRoute>
            <LegacyLetterPage />
          </ProtectedRoute>
        ),
      },
      {
        // NL-024 — generic read-only view of any finalized tool.
        path: 'tools/:toolKey/view',
        element: (
          <ProtectedRoute>
            <FinishedToolView />
          </ProtectedRoute>
        ),
      },
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
];

/**
 * Wrap the router behind a component so consumers don't need to import the
 * `RemixRouter` type from `@remix-run/router` (a transitive dep of react-router).
 * That import is what causes TS2742 ("inferred type is not portable").
 */
export function AppRouter(): JSX.Element {
  return <RouterProvider router={createBrowserRouter(routes)} />;
}
